import { Card, Panel, faDigits } from "@kitchen/ui";
import { useQuery } from "@tanstack/react-query";
import { api, getPendingDevices, type ManagerDashboardRow } from "../../api.js";

function navigate(href: string) {
  history.pushState(null, "", href);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function HomePage() {
  const dashboard = useQuery({
    queryKey: ["manager-dashboard"],
    queryFn: () => api<ManagerDashboardRow>("/forecast/dashboard"),
  });
  const pendingDevices = useQuery({
    queryKey: ["pending-devices"],
    queryFn: getPendingDevices,
    retry: false,
  });

  const data = dashboard.data;
  const pendingCount = pendingDevices.data?.length ?? 0;

  const hasOrders = data?.todayOrders !== undefined;
  const hasForecast = data?.tomorrowForecast !== undefined;
  const hasAlerts = data?.dropAlerts !== undefined;
  const hasKitchen = data?.kitchenTasksLate !== undefined;
  const hasDeliveries = data?.deliveryExceptions !== undefined;
  const hasStock = data?.stockShortages !== undefined;

  const showDashboard = hasOrders || hasForecast || hasAlerts || hasKitchen || hasDeliveries || hasStock;

  return (
    <div className="stack" style={{ gap: "var(--s4)" }}>
      {pendingCount > 0 && (
        <a
          href="/staff"
          data-testid="dashboard-pending-devices"
          onClick={(e) => {
            e.preventDefault();
            navigate("/staff");
          }}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <div
            className="row card"
            style={{
              alignItems: "center",
              justifyContent: "space-between",
              padding: "var(--s3) var(--s4)",
              background: "var(--sand-card)",
              border: "1px solid var(--sand-border)",
              borderRadius: "var(--radius-md)",
            }}
          >
            <div className="row" style={{ alignItems: "center", gap: "var(--s3)" }}>
              <span
                data-testid="pending-devices-badge"
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "24px",
                  height: "24px",
                  padding: "0 6px",
                  borderRadius: "12px",
                  background: "var(--coral-ink, #c0392b)",
                  color: "#fff",
                  fontWeight: "bold",
                  fontSize: "0.85rem",
                }}
              >
                {faDigits(pendingCount)}
              </span>
              <span className="b">دستگاه‌های در انتظار تأیید</span>
              <span className="ink2" style={{ fontSize: "0.9rem" }}>
                برای بررسی و تأیید دستگاه‌های جدید کلیک کنید
              </span>
            </div>
            <span className="ink2">مشاهده در بخش کارکنان ←</span>
          </div>
        </a>
      )}

      {showDashboard && (
        <section aria-label="پیشخوان امروز">
          <h2 className="t-title" style={{ marginBottom: "var(--s2)" }}>
            امروز
          </h2>
          <div
            className="grid"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
              gap: "var(--s3)",
            }}
          >
            {hasOrders && data.todayOrders && (
              <a
                href="/orders"
                data-testid="dashboard-card-orders"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/orders");
                }}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Card>
                  <div className="stack" style={{ gap: "var(--s1)" }}>
                    <span className="hint">سفارش‌های امروز</span>
                    {data.todayOrders.error ? (
                      <span
                        className="t-card num b"
                        style={{ fontSize: "1.1rem", color: "var(--coral-ink)" }}
                      >
                        بارگیری نشد
                      </span>
                    ) : (
                      <>
                        <span className="t-card num b" style={{ fontSize: "1.4rem" }}>
                          {faDigits(data.todayOrders.total)} سفارش
                        </span>
                        <span className="hint ink2" style={{ fontSize: "0.85rem" }}>
                          {faDigits(data.todayOrders.approved)} تأیید شده •{" "}
                          {faDigits(data.todayOrders.inProduction)} در پخت •{" "}
                          {faDigits(data.todayOrders.delivered)} تحویل
                        </span>
                      </>
                    )}
                  </div>
                </Card>
              </a>
            )}

            {hasForecast && data.tomorrowForecast && (
              <a
                href="/forecast"
                data-testid="dashboard-card-forecast"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/forecast");
                }}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Card>
                  <div className="stack" style={{ gap: "var(--s1)" }}>
                    <span className="hint">پیش‌بینی فردا</span>
                    {data.tomorrowForecast.error ? (
                      <span
                        className="t-card num b"
                        style={{ fontSize: "1.1rem", color: "var(--coral-ink)" }}
                      >
                        بارگیری نشد
                      </span>
                    ) : (
                      <>
                        <span className="t-card num b" style={{ fontSize: "1.4rem" }}>
                          {faDigits(data.tomorrowForecast.expectedPortions)} پرس
                        </span>
                        <span className="hint ink2" style={{ fontSize: "0.85rem" }}>
                          ثبت‌شده: {faDigits(data.tomorrowForecast.orderedPortions)} پرس (بازه:{" "}
                          {faDigits(data.tomorrowForecast.lowPortions)} تا{" "}
                          {faDigits(data.tomorrowForecast.highPortions)})
                        </span>
                      </>
                    )}
                  </div>
                </Card>
              </a>
            )}

            {hasAlerts && data.dropAlerts && (
              <a
                href="/forecast"
                data-testid="dashboard-card-alerts"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/forecast");
                }}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Card>
                  <div className="stack" style={{ gap: "var(--s1)" }}>
                    <span className="hint">هشدارهای افت سفارش</span>
                    {data.dropAlerts.error ? (
                      <span
                        className="t-card num b"
                        style={{ fontSize: "1.1rem", color: "var(--coral-ink)" }}
                      >
                        بارگیری نشد
                      </span>
                    ) : (
                      <>
                        <span
                          className="t-card num b"
                          style={{
                            fontSize: "1.4rem",
                            color:
                              data.dropAlerts.criticalCount > 0
                                ? "var(--coral-ink)"
                                : data.dropAlerts.openCount > 0
                                  ? "var(--amber-ink)"
                                  : undefined,
                          }}
                        >
                          {faDigits(data.dropAlerts.openCount)} مورد باز
                        </span>
                        <span className="hint ink2" style={{ fontSize: "0.85rem" }}>
                          {data.dropAlerts.criticalCount > 0
                            ? `${faDigits(data.dropAlerts.criticalCount)} مورد بحرانی`
                            : "نیاز به بررسی توسط مسئول"}
                        </span>
                      </>
                    )}
                  </div>
                </Card>
              </a>
            )}

            {hasKitchen && data.kitchenTasksLate && (
              <a
                href="/production"
                data-testid="dashboard-card-kitchen"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/production");
                }}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Card>
                  <div className="stack" style={{ gap: "var(--s1)" }}>
                    <span className="hint">وظایف با تأخیر آشپزخانه</span>
                    {data.kitchenTasksLate.error ? (
                      <span
                        className="t-card num b"
                        style={{ fontSize: "1.1rem", color: "var(--coral-ink)" }}
                      >
                        بارگیری نشد
                      </span>
                    ) : (
                      <>
                        <span className="t-card num b" style={{ fontSize: "1.4rem" }}>
                          {faDigits(data.kitchenTasksLate.lateCount)} وظیفه
                        </span>
                        <span className="hint ink2" style={{ fontSize: "0.85rem" }}>
                          برنامه‌ریزی یا شروع شده با تأخیر
                        </span>
                      </>
                    )}
                  </div>
                </Card>
              </a>
            )}

            {hasDeliveries && data.deliveryExceptions && (
              <a
                href="/distribution"
                data-testid="dashboard-card-deliveries"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/distribution");
                }}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Card>
                  <div className="stack" style={{ gap: "var(--s1)" }}>
                    <span className="hint">استثناهای تحویل امروز</span>
                    {data.deliveryExceptions.error ? (
                      <span
                        className="t-card num b"
                        style={{ fontSize: "1.1rem", color: "var(--coral-ink)" }}
                      >
                        بارگیری نشد
                      </span>
                    ) : (
                      <>
                        <span
                          className="t-card num b"
                          style={{
                            fontSize: "1.4rem",
                            color:
                              data.deliveryExceptions.shortOrFailedCount > 0 ? "var(--coral-ink)" : undefined,
                          }}
                        >
                          {faDigits(data.deliveryExceptions.shortOrFailedCount)} مورد
                        </span>
                        <span className="hint ink2" style={{ fontSize: "0.85rem" }}>
                          تحویل با کسری یا ناموفق
                        </span>
                      </>
                    )}
                  </div>
                </Card>
              </a>
            )}

            {hasStock && data.stockShortages && (
              <a
                href="/inventory"
                data-testid="dashboard-card-inventory"
                onClick={(e) => {
                  e.preventDefault();
                  navigate("/inventory");
                }}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Card>
                  <div className="stack" style={{ gap: "var(--s1)" }}>
                    <span className="hint">اقلام انبار زیر حداقل</span>
                    {data.stockShortages.error ? (
                      <span
                        className="t-card num b"
                        style={{ fontSize: "1.1rem", color: "var(--coral-ink)" }}
                      >
                        بارگیری نشد
                      </span>
                    ) : (
                      <>
                        <span
                          className="t-card num b"
                          style={{
                            fontSize: "1.4rem",
                            color: data.stockShortages.underMinCount > 0 ? "var(--coral-ink)" : undefined,
                          }}
                        >
                          {faDigits(data.stockShortages.underMinCount)} قلم
                        </span>
                        <span className="hint ink2" style={{ fontSize: "0.85rem" }}>
                          موجودی کمتر از نقطه سفارش
                        </span>
                      </>
                    )}
                  </div>
                </Card>
              </a>
            )}
          </div>
        </section>
      )}

      <Panel title="خوش آمدید">
        <p className="ink2">
          پیشخوان کامل (سفارش‌ها، مسیر امروز، اقدام‌های لازم) در فاز سفارش ساخته می‌شود. فعلاً سازمان‌ها،
          کارکنان، نقش‌ها، آشپزخانه‌ها و تنظیمات از ستون کناری در دسترس‌اند.
        </p>
      </Panel>
    </div>
  );
}
